import { useEffect } from 'react'

// Third party
import { ReactJSXElement } from '@emotion/react/types/jsx-namespace'
import moment from 'moment'
import { useForm } from 'react-hook-form'

// Helpers
import { calculateAge, getValueFromSelectList } from '../../../../models/interface.helper'

import { CountryList } from '../../../../fakeData/countryList'
import { disciplineData } from '../../../../fakeData/disciplineList'
import { zoneList } from '../../../../fakeData/ZoneList'

// Types
import {
  DISCIPLINE_MEMBERSHIP_FIELDS,
  getUserFieldName,
  NumberMembershipFieldKey,
} from '../../../../const/discipline-memberships-const'
import { IUserPublicInterface } from '../../../../models/users-public/users-public.interface'
import { ICompetitorProfileDetailForm } from '../../../../types/competitor_types'

type IGetFieldProps = {
  name: string
  icon: ReactJSXElement | string
  text_to_show?: string
}

// Generate numbers array from common constants
const numbersArray: NumberMembershipFieldKey[] = DISCIPLINE_MEMBERSHIP_FIELDS.map(
  (field) => getUserFieldName(field.key) as NumberMembershipFieldKey
)

const ProfileUserProfileDataTab = ({
  profileDetails,
  userDocumentData,
}: {
  profileDetails: IUserPublicInterface | null
  userDocumentData: any
}) => {
  const { register, watch, reset } = useForm<ICompetitorProfileDetailForm>({
    mode: 'onChange',
  })

  useEffect(() => {
    if (profileDetails !== null) {
      try {
        reset(getResetFormValues())
      } catch (error) {
        console.error(error, 'error')
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileDetails, userDocumentData])

  const getField = ({ name, icon, text_to_show }: IGetFieldProps) => {
    if (watch(name)) {
      return (
        <div className="relative flex mb-2 items-center border-solid border-[#D3DAEE] border overflow-hidden rounded-xl">
          {typeof icon === 'string' ? (
            <span className="mx-4 mr-6 absolute w-6 h-6">
              <img src={icon} alt="icon" className="object-cover w-full h-full" />
            </span>
          ) : (
            icon
          )}

          {text_to_show && <span className="pl-[40px] text-sm">{text_to_show}: </span>}

          <input
            type="text"
            disabled
            readOnly
            {...register(name)}
            className={`border border-transparent text-[14px] text-SeabiscuitDark200ThemeColor h-[56px] capitalize ${text_to_show ? 'pl-0' : 'pl-[62px]'} w-[100%]`}
          />
        </div>
      )
    }
  }
  const getResetFormValues = () => {
    let competitorDocUpdated = {}
    let competitorDocument = userDocumentData ? { ...userDocumentData } : {}

    const userNationality =
      getValueFromSelectList({
        list: CountryList,
        valueToFind: profileDetails?.userNationality ?? '',
      })?.label ?? ''

    const userDiscipline =
      getValueFromSelectList({
        list: disciplineData,
        valueToFind: profileDetails?.userDiscipline ?? '',
      })?.label ?? ''

    const userZone =
      getValueFromSelectList({
        list: zoneList,
        valueToFind: profileDetails?.userZone ?? '',
      })?.label ?? ''

    Object.keys(competitorDocument).forEach((curr) => {
      if (typeof userDocumentData[curr] === 'object')
        competitorDocUpdated = { ...competitorDocUpdated, [curr]: userDocumentData[curr]?.number }
    })

    return {
      ...profileDetails,
      ...competitorDocUpdated,
      userDOB: `${calculateAge(profileDetails?.userDOB ?? '')} years old`,
      userZone: `Zone ${userZone}`,
      userDiscipline,
      userNationality,
      userForeignEndorsementFormatted: `FE Letter${profileDetails?.userForeignEndorsement?.validTill ? ' Expires: ' + moment(profileDetails?.userForeignEndorsement?.validTill).format('MMMM, DD YYYY') : ': No Letter on File'}`,
    }
  }

  return (
    <>
      <div className="mb-5 text-SeabiscuitLightParagraphTextColor">
        <div className="heading uppercase mb-3 font-md text-SeabiscuitDark200ThemeColor">
          PERSONAL DETAILS
        </div>
        {getField({
          name: 'userName',
          icon: 'assets/og_icons/User.svg',
        })}
        {getField({
          name: 'userDOB',
          icon: 'assets/og_icons/Calendar.svg',
        })}
        {getField({
          name: 'userNationality',
          icon: 'assets/og_icons/Globe.svg',
        })}
      </div>

      <div className="mb-5 text-SeabiscuitLightParagraphTextColor">
        <div className="heading uppercase mb-3 font-md text-SeabiscuitDark200ThemeColor">
          COMPETITION DETAILS
        </div>
        {getField({
          name: 'userDiscipline',
          icon: '/assets/og_icons/YearofHorse.svg',
        })}
        {getField({
          name: 'userZone',
          icon: '/assets/img/light/Zone.svg',
        })}
        {getField({
          name: 'userAmateur',
          icon: '/assets/og_icons/ProfessionalStatus.svg',
        })}
        {getField({
          name: 'userForeignEndorsementFormatted',
          icon: '/assets/cp_icons/letter-1.svg',
        })}
      </div>

      <div className="text-SeabiscuitLightParagraphTextColor">
        <div className="heading uppercase mb-3 font-md text-SeabiscuitDark200ThemeColor">
          COMPETITION NUMBERS
        </div>
        {numbersArray?.map((number, index) => {
          const document = profileDetails?.[number]
          // Find the discipline field info to get the proper icon
          const disciplineField = DISCIPLINE_MEMBERSHIP_FIELDS.find(
            (field) => getUserFieldName(field.key) === number
          )

          return (
            document?.documentNumber && (
              <div
                key={index}
                className="relative flex mb-2 items-center border-[#D3DAEE] border-solid border overflow-hidden rounded-xl"
              >
                <span className="p-[5px] w-[58px] h-[52px] ml-1 rounded-[60%]">
                  <img
                    src={
                      disciplineField?.icon ||
                      `/assets/${number.replace('user', '').toLowerCase()}.svg`
                    }
                    alt="icon"
                    className="object-cover"
                  />
                </span>
                {document?.documentNumber !== '' ? (
                  <input
                    type="text"
                    disabled
                    readOnly
                    value={`${disciplineField?.label || number.replace('user', '')} : ${document?.documentNumber}`}
                    className={`border border-transparent text-[14px] text-SeabiscuitDark200ThemeColor h-[56px] pl-3 w-[100%]`}
                  />
                ) : (
                  <span
                    className={`border border-transparent text-[14px] text-SeabiscuitDark200ThemeColor opacity-50 h-[56px] pl-3 w-[100%] flex items-center`}
                  >
                    No {disciplineField?.label || number.replace('user', '')} number
                  </span>
                )}
              </div>
            )
          )
        })}
        {profileDetails?.userExtraDocument?.map(
          (userExtraDocument) =>
            userExtraDocument?.documentNumber && (
              <div
                key={userExtraDocument.documentFullName}
                className="relative flex mb-2 items-center border-[#D3DAEE] border-solid border overflow-hidden rounded-xl"
              >
                {
                  <span className="p-[5px] w-[58px] h-[52px] ml-1 rounded-[60%]">
                    <img
                      src={`/assets/defaultcompetitionlogo.svg`}
                      alt="icon"
                      className="object-cover"
                    />
                  </span>
                }
                {userExtraDocument.documentNumber !== '' ? (
                  <input
                    type="text"
                    disabled
                    readOnly
                    value={` ${userExtraDocument.documentFullName} : ${userExtraDocument.documentNumber}`}
                    className={`border border-transparent text-[14px] text-SeabiscuitDark200ThemeColor h-[56px] pl-3 w-[100%]`}
                  />
                ) : (
                  <>
                    <span
                      className={`border border-transparent text-[14px] text-SeabiscuitDark200ThemeColor opacity-50 h-[56px] pl-3 w-[100%] flex items-center`}
                    >
                      No number
                    </span>
                  </>
                )}
              </div>
            )
        )}
      </div>
    </>
  )
}

export default ProfileUserProfileDataTab
